import { mainLogger as log } from "@/app/logger"
import { Pulse } from "@/app/types"
import { FullScreenPopup } from "@/components/popups/popup.fullscreen_chart"
import * as ChartCommons from "@/pages/charts/highcharts/common"
import { renderFullScreenButton } from "@/pages/charts/highcharts/full_screen_button_renderer"
import { theme } from "@/tailwind.config"
import { formatDateString } from "@/utils/format_date_string"
import HighchartsReact from "highcharts-react-official"
import HighchartsMore from "highcharts/highcharts-more"
import Highcharts from "highcharts/highstock"
import HighchartsAnnotations from "highcharts/modules/annotations"
import { useState } from "react"

interface ChartProps extends Partial<Pulse> {
  categories: string[]
  data: {
    name: string
    data: number[]
  }[]
  percent: boolean
  currency: boolean
  stacked: boolean
  stacking?: string
  type: string
  disclaimer: string
  height?: number
  chartLabelsFormat?: string[]
}

export function PulseChart({
  categories,
  data,
  currency,
  percent,
  stacked,
  stacking,
  type,
  disclaimer,
  height,
  chartLabelsFormat,
  ...pulse
}: ChartProps) {
  const [isOpen, setIsOpen] = useState(false)

  const {
    xAxis_max_range,
    xAxis_min_range,
    yAxis_max_range,
    yAxis_min_range,
    invert_xAxis,
    invert_yAxis,
  } = pulse

  const screenWidth = window.screen.width
  const screenInnerHeight = window.innerHeight
  const isScreenMobile = screenWidth <= 768

  const USD_FORMATTER = new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    currency: "USD",
    signDisplay: "never",
  })

  const PCENT_FORMATTER = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const PCENT_TWO_FORMATTER = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const NUMBER_FORMATTER = new Intl.NumberFormat("pt-BR", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })

  // using a map to select proper formatter in case of multiple column labels
  const formatterMap = new Map()
  formatterMap.set("currency", USD_FORMATTER)
  formatterMap.set("percent", PCENT_FORMATTER)
  formatterMap.set("percent_two", PCENT_TWO_FORMATTER)
  formatterMap.set("percentile", NUMBER_FORMATTER)

  log.debug("Data for chart", { data, categories, percent, stacked, type })

  const colors = {
    default: [
      theme.colors.primary[700],
      theme.colors.primary[500],
      theme.colors.primary[300],
      theme.colors.tertiary[700],
      theme.colors.tertiary[500],
      theme.colors.tertiary[300],
      theme.colors.main[700],
      theme.colors.main[500],
      theme.colors.main[300],
      theme.colors.secondary[500],
    ],
    line_column: [
      theme.colors.primary[700],
      theme.colors.primary[500],
      theme.colors.primary[300],
      theme.colors.tertiary[700],
      theme.colors.tertiary[500],
      theme.colors.tertiary[300],
      theme.colors.main[700],
      theme.colors.main[500],
      theme.colors.main[300],
      theme.colors.secondary[500],
    ],
    stacked: {
      column: [
        theme.colors.primary[700],
        theme.colors.primary[500],
        theme.colors.primary[300],
        theme.colors.tertiary[700],
        theme.colors.tertiary[500],
        theme.colors.tertiary[300],
        theme.colors.main[700],
        theme.colors.main[500],
        theme.colors.main[300],
        theme.colors.secondary[500],
      ],
    },
  }

  // fixed colors for this categories specifically
  const categoryColors: Record<string, string> = {
    "Active ETF": theme.colors.primary[700],
    "Index ETF": theme.colors.primary[300],
    "Active MF": theme.colors.tertiary[700],
    "Index MF": theme.colors.tertiary[300],
  }

  const isPlot = type === "bubble" || type === "scatter"

  const series = (() => {
    if (isPlot) {
      return [
        {
          data: data.map((series, i) => ({
            name: series.name,
            x: series.data[0],
            y: series.data[1],
            z: series.data[2],
          })),
        },
      ]
    }

    const result = data.map((series, i) => ({
      ...series,
      color: (() => {
        const set =
          (stacked ? colors.stacked[type] : null) ??
          colors[type] ??
          colors.default

        return categoryColors[series.name] || set[i % set.length]
      })(),
      // if stacked, then use stacking if defined else default to "normal"
      stacking: stacked ? stacking ?? "normal" : undefined,
      type: (() => {
        if (type === "line_column") {
          return i === 0 ? "column" : "line"
        }
        return type
      })(),
      borderWidth: 0,
      groupPadding: 0.1,
    }))

    if (stacked) return result.toReversed()

    return result
  })()

  const options = {
    ...ChartCommons.TOP_LEVEL_OPTIONS,

    chart: {
      ...ChartCommons.CHART_OPTIONS,
      type,
      height: isScreenMobile ? 500 : height ?? 700,
      // allow zooming on y-axis for line-column
      zooming: {
        type: "xy",
        pinchType: "xy",
        resetButton: {
          theme: {
            fill: theme.colors.main[300],
            stroke: theme.colors.main[700],
            zIndex: 7,
            style: {
              fontSize: "0.75rem",
              color: theme.colors["main-dark"],
              fontWeight: 600,
            },
            states: {
              hover: {
                fill: theme.colors.primary[600],
                style: {
                  color: theme.colors["main-light"],
                },
              },
            },
          },
        },
      },
      events: {
        load: function () {
          ChartCommons.createResetZoomButtonEvents(this)

          if (type === "bar") {
            ChartCommons.addAsOfLabel(
              this,
              disclaimer,
              stacked ? 0
              : isScreenMobile ? 0
              : this.plotSizeX,
              this.plotSizeY,
              "0.6rem"
            )
          } else {
            ChartCommons.addAsOfLabel(
              this,
              disclaimer,
              this.plotLeft,
              this.plotSizeY + 20,
              "0.6rem"
            )
          }
        },
        render: function () {
          renderFullScreenButton({ chart: this, isOpen, setIsOpen })

          ChartCommons.redimChartForPrint(isScreenMobile)
        },
      },
    },

    series,

    legend: {
      enabled: series.length > 1,
      reversed: stacked,
      symbolRadius: 0,
      squareSymbol: true,
      verticalAlign: "top",
      itemDistance: 40,
      itemMarginTop: 8,
      itemStyle: {
        color: theme.colors.main[500],
        cursor: "pointer",
      },
    },

    plotOptions: {
      bar: {
        pointWidth: isScreenMobile ? 15 : 20,
      },
      line: {
        marker: false,
      },
      bubble: {
        dataLabels: {
          style: {
            color: "black",
            textOutline: "none",
            fontWeight: "normal",
            fontSize: "0.6rem",
          },
        },
      },
    },

    xAxis: [
      {
        title: false,
        minRange: xAxis_min_range ?? undefined,
        maxRange: xAxis_max_range ?? undefined,
        reversed: invert_xAxis ?? undefined,
        labels: {
          ...ChartCommons.X_AXES_LABELS,
          // format: isPlot ? "{value}%" : null,
          formatter:
            isPlot || percent || type === "bar" ?
              undefined
            : function () {
                return formatDateString(this.value)
              },
          style: {
            ...ChartCommons.X_AXES_LABELS.style,
            color: theme.colors.main[500],
          },
        },
        categories: isPlot ? null : categories,
        crosshair: {
          ...ChartCommons.CROSSHAIR_OPTIONS,
          zIndex: 3,
        },
        scrollbar: { enabled: true, liveDraw: true },
        tickPositioner:
          ["bar", "bubble", "scatter"].includes(type) ? undefined : (
            function () {
              const ticksLength = series[0].data.length
              let ticks: number[] = []

              if (ticksLength <= 5)
                return Array.from({ length: ticksLength }, (_, index) => index)

              let step = 1
              if (ticksLength > 5 && ticksLength <= 12) {
                step = 2
              } else if (ticksLength > 12 && ticksLength <= 36) {
                step = isScreenMobile ? 4 : 3
              } else if (ticksLength > 36 && ticksLength <= 72) {
                step = 6
              } else if (ticksLength > 72) {
                step =
                  isScreenMobile ?
                    Math.ceil(ticksLength / 10)
                  : Math.ceil(ticksLength / 8)
              }

              for (let i = ticksLength - 1; i >= 0; i -= step) {
                ticks.push(i)
              }

              log.info("Returning xAxis ticks:", ticks)
              return ticks
            }
          ),
      },
    ],

    yAxis: [
      {
        title: false,
        plotLines: null,
        minRange: yAxis_min_range ?? undefined,
        maxRange: yAxis_max_range ?? undefined,
        reversed: invert_yAxis ?? undefined,
        crosshair: {
          ...ChartCommons.CROSSHAIR_OPTIONS,
          zIndex: 3,
        },
        labels: {
          formatter: function () {
            if (percent) {
              return stacked ?
                  PCENT_FORMATTER.format(this.value / 100)
                : PCENT_FORMATTER.format(this.value)
            } else if (currency) {
              return this.value >= 0 ?
                  `$${USD_FORMATTER.format(this.value)}`
                : `-$${USD_FORMATTER.format(this.value)}`
            } else {
              return NUMBER_FORMATTER.format(this.value)
            }
          },
          style: {
            color: theme.colors.main[500],
          },
        },
        scrollbar: { enabled: true, liveDraw: true },
      },
    ],

    tooltip: {
      enabled: true,
      split: false,
      shared: false,
      useHTML: true,
      formatter: function () {
        let s =
          "<b>" +
          (isPlot ? this.point.name : this.x) +
          (stacked ? " (" + this.series.name + ")" : "") +
          "</b><br/>"

        if (isPlot && chartLabelsFormat) {
          const formatter = formatterMap.get(chartLabelsFormat[0])
          s += `${categories[0]} - ${formatter.format(this.point.x)}`
          if (typeof this.point.y !== "undefined") {
            const formatter = formatterMap.get(chartLabelsFormat[1])
            s += `<br/> ${categories[1]} - ${formatter.format(this.point.y)}`
          }
          if (typeof this.point.z !== "undefined") {
            const formatter = formatterMap.get(chartLabelsFormat[2])
            s += `<br/> ${categories[2]} - ${formatter.format(this.point.z)}`
          }
        } else if (percent) {
          s += `${PCENT_FORMATTER.format(this.y)}`
        } else if (currency) {
          s +=
            this.y > 0 ?
              `$${USD_FORMATTER.format(this.y)}`
            : `-$${USD_FORMATTER.format(this.y)}`
        } else {
          s += `${NUMBER_FORMATTER.format(this.y)}`
        }

        return s
      },
    },
  }

  log.debug("Plotting chart with options", options)

  if (!Highcharts) return <>ERR-00 Cannot load charts</>

  HighchartsMore(Highcharts)
  HighchartsAnnotations(Highcharts)

  const newOptions = {
    ...options,
    chart: {
      ...options.chart,
      height: screenWidth > 1200 ? window.innerHeight - 200 : screenInnerHeight,
    },
  }

  return (
    <FullScreenPopup isOpen={isOpen} close={() => setIsOpen(false)}>
      <HighchartsReact
        highcharts={Highcharts}
        options={isOpen ? newOptions : options}
        immutable={true}
      />
    </FullScreenPopup>
  )
}
